import { useParams } from "@tanstack/react-router";
import { Loader2, Clock, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { usePublicStoreBranding, usePublicReturnStatus } from "@/hooks/use-returns";
import { isExchangeRequest } from "@/lib/return-labels";
import { readableAccent } from "@/lib/accent";

const STATUS_LABEL: Record<string, string> = {
  new: "Zaprimljeno",
  in_progress: "U obradi",
  approved: "Odobreno",
  rejected: "Odbijeno",
  refund_completed: "Povrat izvršen",
  exchange_completed: "Zamjena izvršena",
};

function statusDescriptions(isExchange: boolean): Record<string, string> {
  if (isExchange) {
    return {
      new: "Vaš zahtjev za zamjenu je zaprimljen i čeka obradu.",
      in_progress: "Vaš zahtjev je u obradi. Kontaktirat ćemo vas ukoliko bude potrebno.",
      approved: "Vaš zahtjev za zamjenu je odobren. Uskoro šaljemo zamjenski artikal.",
      rejected: "Vaš zahtjev za zamjenu je odbijen.",
      exchange_completed: "Zamjena je izvršena. Hvala vam na strpljenju.",
    };
  }
  return {
    new: "Vaš zahtjev za povrat je zaprimljen i čeka obradu.",
    in_progress: "Vaš zahtjev je u obradi. Kontaktirat ćemo vas ukoliko bude potrebno.",
    approved: "Vaš zahtjev za povrat je odobren. Povrat novca će biti izvršen uskoro.",
    rejected: "Vaš zahtjev za povrat je odbijen.",
    refund_completed: "Povrat novca je izvršen. Hvala vam na strpljenju.",
  };
}

export function PublicReturnStatusPage() {
  const { slug, ref } = useParams({ strict: false });
  const { data: store, isLoading: storeLoading } = usePublicStoreBranding(slug ?? null);
  const { data: status, isLoading: statusLoading, isError } = usePublicReturnStatus(slug ?? null, ref ?? null);

  if (storeLoading || statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !store || !status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Zahtjev nije pronađen.</p>
          <a href={`/return/${slug}`} className="text-sm text-blue-400 hover:underline">
            Pošalji novi zahtjev →
          </a>
        </div>
      </div>
    );
  }

  const color = readableAccent(store.primaryColor, "#4F46E5");
  const isExchange = isExchangeRequest(status.requestType);
  const completionStatus = isExchange ? "exchange_completed" : "refund_completed";
  const STATUS_FLOW = ["new", "in_progress", "approved", completionStatus];
  const STATUS_DESCRIPTION = statusDescriptions(isExchange);
  const isRejected = status.status === "rejected";
  const currentIndex = isRejected ? 1 : STATUS_FLOW.indexOf(status.status);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div>
          {store.logoUrl ? (
            <img src={store.logoUrl} alt={store.name} className="w-20 h-20 mx-auto mb-2 object-contain" />
          ) : (
            <div className="w-20 h-20 mx-auto mb-2 flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: color }}>
              {store.name.charAt(0)}
            </div>
          )}
          <h1 className="font-semibold">{store.name}</h1>
          <p className="text-xs text-muted-foreground mt-1">Status zahtjeva za povrat</p>
        </div>

        <div className="inline-block border border-dashed  px-6 py-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Referentni broj</p>
          <p className="text-xl font-bold tracking-wider" style={{ color }}>{status.referenceNumber}</p>
        </div>

        <div className="flex flex-col items-stretch gap-0 w-full max-w-xs mx-auto">
          {STATUS_FLOW.map((s, i) => {
            const isActive = i <= currentIndex && !isRejected;
            const isCurrent = i === currentIndex && !isRejected;
            return (
              <div key={s} className="flex flex-col items-center">
                {i > 0 && (
                  <div className="h-4 w-px bg-border" />
                )}
                <div
                  className={`flex items-center justify-center gap-1 w-full px-3 py-1.5 text-[11px] font-medium border ${
                    isCurrent
                      ? "text-white"
                      : isActive
                      ? "text-muted-foreground border-border bg-muted/50"
                      : "text-muted-foreground/40 border-border/50"
                  }`}
                  style={isCurrent ? { backgroundColor: color, borderColor: color } : {}}
                >
                  {isCurrent && (s === completionStatus || s === "approved" ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />)}
                  {STATUS_LABEL[s]}
                </div>
              </div>
            );
          })}
        </div>

        {isRejected && (
          <div className="flex items-center justify-center gap-1.5 text-red-400">
            <XCircle className="h-4 w-4" />
            <span className="text-sm font-medium">{STATUS_LABEL.rejected}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {STATUS_DESCRIPTION[status.status]}
        </p>
      </div>
    </div>
  );
}
